import { useState } from "react";
import { Copy, Check, Flame, TrendingUp, Target, PenLine, CalendarDays } from "lucide-react";
import { MESSAGE_TEMPLATES, COMMITMENTS, DISCLAIMER } from "./courseData";

const NAVY = "#001540";
const GOLD = "#f5c84c";
const GREEN = "#a7cb90";

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const daysAgoStr = (n) => { const d = new Date(Date.now() - n * 86400000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const Disclaimer = () => (
  <p className="mt-4 text-[11px] leading-relaxed text-slate-500 bg-slate-100 border border-slate-200 rounded-xl p-3" data-testid="sf-disclaimer">
    {DISCLAIMER}
  </p>
);

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-[#001540] mb-1.5">{label}</span>
    <input
      className="w-full rounded-xl border-2 border-slate-200 focus:border-[#f5c84c] outline-none px-4 py-3 text-base font-medium text-[#1a1f36] transition-colors"
      {...props}
    />
  </label>
);

/* ─────────── 1. Calculadora de utilidad ─────────── */
export function UtilidadCalc({ value = {}, onChange }) {
  const pot = Number(value.potenciales) || 0;
  const cierre = Number(value.cierre) || 0;
  const ganancia = Number(value.ganancia) || 0;
  const cerrados = Math.round((pot * cierre) / 100);
  const utilidad = cerrados * ganancia;
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const maxBar = Math.max(pot, cerrados, 1);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="sf-tool-utilidad">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-[#f5c84c]" />
        <h4 className="text-lg font-extrabold text-[#001540]">Calculadora de utilidad</h4>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Clientes potenciales / mes" type="number" min="0" inputMode="numeric" value={value.potenciales ?? ""} onChange={set("potenciales")} placeholder="20" data-testid="sf-util-potenciales" />
        <Field label="% de cierre" type="number" min="0" max="100" inputMode="numeric" value={value.cierre ?? ""} onChange={set("cierre")} placeholder="10" data-testid="sf-util-cierre" />
        <Field label="Ganancia por venta ($)" type="number" min="0" inputMode="numeric" value={value.ganancia ?? ""} onChange={set("ganancia")} placeholder="100" data-testid="sf-util-ganancia" />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl p-4 text-center" style={{ background: "#a7cb9022" }}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clientes cerrados</p>
          <p className="text-3xl font-black text-[#001540] mt-1" data-testid="sf-util-cerrados">{cerrados}</p>
        </div>
        <div className="rounded-xl p-4 text-center" style={{ background: "#f5c84c22" }}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Utilidad del mes</p>
          <p className="text-3xl font-black text-[#001540] mt-1" data-testid="sf-util-utilidad">${utilidad.toLocaleString()}</p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {[{ l: "Potenciales", v: pot, c: NAVY }, { l: "Cerrados", v: cerrados, c: GREEN }].map((b) => (
          <div key={b.l}>
            <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1"><span>{b.l}</span><span>{b.v}</span></div>
            <div className="h-4 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(b.v / maxBar) * 100}%`, background: b.c }} />
            </div>
          </div>
        ))}
      </div>
      <Disclaimer />
    </div>
  );
}

/* ─────────── 2. Planificador de rutina 90 min ─────────── */
const BLOCKS = [
  { key: "prospectar", label: "Prospectar", min: 30 },
  { key: "seguimiento", label: "Seguimiento", min: 30 },
  { key: "aprender", label: "Aprender", min: 20 },
  { key: "medir", label: "Medir", min: 10 },
];

export function RutinaPlanner({ value = {}, onChange }) {
  const horarios = value.horarios || {};
  const doneToday = value.doneToday && value.doneToday.date === todayStr() ? value.doneToday.blocks || {} : {};
  const streak = value.streak || 0;

  const setHorario = (k) => (e) => onChange({ ...value, horarios: { ...horarios, [k]: e.target.value } });

  const toggleBlock = (k) => {
    const blocks = { ...doneToday, [k]: !doneToday[k] };
    const allDone = BLOCKS.every((b) => blocks[b.key]);
    let newStreak = streak;
    let lastDate = value.lastDate;
    if (allDone && value.lastDate !== todayStr()) {
      const yest = daysAgoStr(1);
      newStreak = value.lastDate === yest ? streak + 1 : 1;
      lastDate = todayStr();
    }
    onChange({ ...value, doneToday: { date: todayStr(), blocks }, streak: newStreak, lastDate });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="sf-tool-rutina">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-extrabold text-[#001540]">Rutina de 90 minutos</h4>
        {streak > 0 && (
          <span className="inline-flex items-center gap-1.5 bg-[#f5c84c]/20 text-[#001540] font-bold text-sm px-3 py-1.5 rounded-full" data-testid="sf-rutina-streak">
            <Flame className="h-4 w-4 text-orange-500" /> Llevas {streak} {streak === 1 ? "día" : "días"} seguidos
          </span>
        )}
      </div>
      <div className="space-y-3">
        {BLOCKS.map((b) => (
          <div key={b.key} className={`flex items-center gap-3 rounded-xl border-2 p-3 transition-colors ${doneToday[b.key] ? "border-[#a7cb90] bg-[#a7cb90]/10" : "border-slate-200"}`}>
            <button
              onClick={() => toggleBlock(b.key)}
              data-testid={`sf-rutina-check-${b.key}`}
              className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${doneToday[b.key] ? "bg-[#a7cb90] text-white" : "bg-slate-100 text-slate-400"}`}
            >
              <Check className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#001540]">{b.label} <span className="text-slate-400 font-medium">· {b.min} min</span></p>
            </div>
            <input type="time" value={horarios[b.key] || ""} onChange={setHorario(b.key)} data-testid={`sf-rutina-hora-${b.key}`}
              className="rounded-lg border-2 border-slate-200 focus:border-[#f5c84c] outline-none px-2 py-1.5 text-sm font-medium text-[#1a1f36]" />
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-500">Marca cada bloque al terminarlo. Completa los 4 cada día para alimentar tu racha. 🔥</p>
    </div>
  );
}

/* ─────────── 3. Plantillas de mensajes ─────────── */
export function Plantillas() {
  const [copied, setCopied] = useState(-1);
  const copy = async (txt, i) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(txt);
      else { const ta = document.createElement("textarea"); ta.value = txt; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }
      setCopied(i);
      setTimeout(() => setCopied(-1), 1800);
    } catch { /* ignore */ }
  };
  return (
    <div className="space-y-3" data-testid="sf-tool-plantillas">
      {MESSAGE_TEMPLATES.map((t, i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-sm font-extrabold text-[#001540]">{t.label}</p>
            <button onClick={() => copy(t.text, i)} data-testid={`sf-plantilla-copy-${i}`}
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
              style={copied === i ? { background: GREEN, color: "#fff" } : { background: "#f5c84c22", color: NAVY }}>
              {copied === i ? <><Check className="h-3.5 w-3.5" /> Copiado</> : <><Copy className="h-3.5 w-3.5" /> Copiar</>}
            </button>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{t.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ─────────── 4. Diario de ventas ─────────── */
export function DiarioVentas({ value = {}, onChange }) {
  const entries = value.entries || [];
  const [form, setForm] = useState({ contactos: "", conversaciones: "", cotizaciones: "", ventas: "", funciono: "", nofunciono: "", distinto: "" });
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = () => {
    const entry = { date: todayStr(), ...form };
    onChange({ ...value, entries: [entry, ...entries].slice(0, 120) });
    setForm({ contactos: "", conversaciones: "", cotizaciones: "", ventas: "", funciono: "", nofunciono: "", distinto: "" });
  };

  const sum = (arr, k) => arr.reduce((a, e) => a + (Number(e[k]) || 0), 0);
  const weekAgo = daysAgoStr(7);
  const monthAgo = daysAgoStr(30);
  const week = entries.filter((e) => e.date >= weekAgo);
  const month = entries.filter((e) => e.date >= monthAgo);
  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

  const Summary = ({ label, data }) => {
    const c = sum(data, "contactos"), cv = sum(data, "conversaciones"), co = sum(data, "cotizaciones"), v = sum(data, "ventas");
    return (
      <div className="rounded-xl border border-slate-200 p-4">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
        <div className="grid grid-cols-4 gap-2 text-center mb-3">
          {[["Contactos", c], ["Conversaciones", cv], ["Cotizaciones", co], ["Ventas", v]].map(([l, n]) => (
            <div key={l}><p className="text-xl font-black text-[#001540]">{n}</p><p className="text-[10px] text-slate-400 leading-tight">{l}</p></div>
          ))}
        </div>
        <div className="space-y-1 text-xs text-slate-500">
          <p>Conversaciones ÷ Contactos: <strong className="text-[#001540]">{pct(cv, c)}%</strong></p>
          <p>Cotizaciones ÷ Conversaciones: <strong className="text-[#001540]">{pct(co, cv)}%</strong></p>
          <p>Ventas ÷ Cotizaciones: <strong className="text-[#001540]">{pct(v, co)}%</strong></p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="sf-tool-diario">
      <div className="flex items-center gap-2 mb-4"><CalendarDays className="h-5 w-5 text-[#f5c84c]" /><h4 className="text-lg font-extrabold text-[#001540]">Diario de ventas · {todayStr()}</h4></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Contactos" type="number" min="0" value={form.contactos} onChange={setF("contactos")} data-testid="sf-diario-contactos" />
        <Field label="Conversaciones" type="number" min="0" value={form.conversaciones} onChange={setF("conversaciones")} data-testid="sf-diario-conversaciones" />
        <Field label="Cotizaciones" type="number" min="0" value={form.cotizaciones} onChange={setF("cotizaciones")} data-testid="sf-diario-cotizaciones" />
        <Field label="Ventas" type="number" min="0" value={form.ventas} onChange={setF("ventas")} data-testid="sf-diario-ventas" />
      </div>
      <div className="mt-3 space-y-3">
        {[["¿Qué funcionó hoy?", "funciono"], ["¿Qué no funcionó?", "nofunciono"], ["¿Qué haré distinto mañana?", "distinto"]].map(([l, k]) => (
          <label key={k} className="block">
            <span className="block text-sm font-semibold text-[#001540] mb-1.5">{l}</span>
            <textarea rows={2} value={form[k]} onChange={setF(k)} data-testid={`sf-diario-${k}`}
              className="w-full rounded-xl border-2 border-slate-200 focus:border-[#f5c84c] outline-none px-4 py-2.5 text-sm text-[#1a1f36]" />
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="sf-diario-guardar"
        className="mt-4 w-full rounded-xl py-3 font-extrabold text-[#001540] transition-transform active:scale-[0.98]" style={{ background: GOLD }}>
        Guardar el día
      </button>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Summary label="Resumen semanal" data={week} />
        <Summary label="Resumen mensual" data={month} />
      </div>
    </div>
  );
}

/* ─────────── 5. Calculadora de meta al revés ─────────── */
export function MetaCalc({ value = {}, onChange }) {
  const ganar = Number(value.ganar) || 0;
  const porVenta = Number(value.porVenta) || 0;
  const cierre = Number(value.cierre) || 0;
  const dias = Number(value.dias) || 0;
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  const ventas = porVenta > 0 ? Math.ceil(ganar / porVenta) : 0;
  const potenciales = cierre > 0 ? Math.ceil(ventas / (cierre / 100)) : 0;
  const personasDia = dias > 0 ? Math.ceil(potenciales / dias) : 0;
  const ready = ganar && porVenta && cierre && dias;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="sf-tool-meta">
      <div className="flex items-center gap-2 mb-4"><Target className="h-5 w-5 text-[#f5c84c]" /><h4 className="text-lg font-extrabold text-[#001540]">Calculadora de meta al revés</h4></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="¿Cuánto quiero ganar este mes? ($)" type="number" min="0" value={value.ganar ?? ""} onChange={set("ganar")} placeholder="600" data-testid="sf-meta-ganar" />
        <Field label="Ganancia promedio por venta ($)" type="number" min="0" value={value.porVenta ?? ""} onChange={set("porVenta")} placeholder="100" data-testid="sf-meta-porventa" />
        <Field label="% de cierre (si empiezas, usa 10)" type="number" min="1" max="100" value={value.cierre ?? ""} onChange={set("cierre")} placeholder="10" data-testid="sf-meta-cierre" />
        <Field label="Días que trabajaré este mes" type="number" min="1" max="31" value={value.dias ?? ""} onChange={set("dias")} placeholder="20" data-testid="sf-meta-dias" />
      </div>

      {ready ? (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl p-3" style={{ background: "#a7cb9022" }}><p className="text-xs text-slate-500 font-semibold">Ventas necesarias</p><p className="text-2xl font-black text-[#001540]">{ventas}</p></div>
            <div className="rounded-xl p-3" style={{ background: "#a7cb9022" }}><p className="text-xs text-slate-500 font-semibold">Clientes potenciales</p><p className="text-2xl font-black text-[#001540]">{potenciales}</p></div>
          </div>
          <div className="mt-3 rounded-2xl p-6 text-center" style={{ background: NAVY }} data-testid="sf-meta-resultado">
            <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: GOLD }}>Tu meta diaria</p>
            <p className="text-4xl sm:text-5xl font-black text-white mt-1">Hablar con {personasDia} personas</p>
            <p className="text-slate-300 text-sm mt-2">cada día de trabajo. Así de alcanzable es tu meta. 💪</p>
          </div>
        </>
      ) : (
        <p className="mt-4 text-sm text-slate-500">Completa los 4 campos para ver tu meta diaria.</p>
      )}
      <Disclaimer />
    </div>
  );
}

/* ─────────── 6. Compromiso Sales First ─────────── */
export function Compromiso({ value = {}, onChange }) {
  const checks = value.checks || {};
  const toggle = (i) => onChange({ ...value, checks: { ...checks, [i]: !checks[i] } });
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="sf-tool-compromiso">
      <div className="flex items-center gap-2 mb-4"><PenLine className="h-5 w-5 text-[#f5c84c]" /><h4 className="text-lg font-extrabold text-[#001540]">Mi compromiso Sales First</h4></div>
      <div className="space-y-2">
        {COMMITMENTS.map((c, i) => (
          <button key={i} onClick={() => toggle(i)} data-testid={`sf-compromiso-check-${i}`}
            className={`w-full text-left flex items-start gap-3 rounded-xl border-2 p-3 transition-colors ${checks[i] ? "border-[#a7cb90] bg-[#a7cb90]/10" : "border-slate-200"}`}>
            <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center mt-0.5 ${checks[i] ? "bg-[#a7cb90] text-white" : "bg-slate-100 text-slate-300"}`}><Check className="h-4 w-4" /></span>
            <span className="text-sm font-medium text-[#1a1f36]">{c}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <Field label="Mi número diario de personas" type="number" min="1" value={value.numeroDiario ?? ""} onChange={set("numeroDiario")} placeholder="3" data-testid="sf-compromiso-numero" />
        <Field label="Mi firma (escribe tu nombre)" type="text" value={value.firma ?? ""} onChange={set("firma")} placeholder="Tu nombre" data-testid="sf-compromiso-firma" />
      </div>
    </div>
  );
}

export const TOOLS = {
  utilidad: UtilidadCalc,
  rutina: RutinaPlanner,
  plantillas: Plantillas,
  diario: DiarioVentas,
  meta: MetaCalc,
  compromiso: Compromiso,
};
